import React, { useState, useEffect } from 'react';
import { X, Building2, Plus, Trash2, Search, Check, Users, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';

const ClientAssignmentModal = ({ isOpen, onClose, user, onUpdate }) => {
  const [assignedClients, setAssignedClients] = useState([]);
  const [availableClients, setAvailableClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedClientsToAdd, setSelectedClientsToAdd] = useState([]);
  const [isAdding, setIsAdding] = useState(false);

  useEffect(() => {
    if (isOpen && user) {
      fetchClientAssignments();
      // Clear any previous selections when modal opens
      setSelectedClientsToAdd([]);
      setSearchTerm('');
    }
  }, [isOpen, user]);

  const fetchClientAssignments = async () => {
    setLoading(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const response = await fetch(`${BACKEND_URL}/api/recruiter-clients/${user.id}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch client assignments');
      }
      
      const data = await response.json();
      setAssignedClients(data.assigned_clients || []);
      setAvailableClients(data.available_clients || []);
    } catch (error) {
      console.error('Error fetching client assignments:', error);
      toast.error('Failed to load client assignments');
    } finally {
      setLoading(false);
    }
  };

  const handleAddClients = async () => {
    if (selectedClientsToAdd.length === 0) {
      toast.error('Please select at least one client');
      return;
    }

    setIsAdding(true);
    
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      
      // Track which clients were added and which were already assigned
      const results = {
        added: [],
        alreadyAssigned: [],
        failed: []
      };
      
      // Show initial toast
      const loadingToast = toast.loading(`Adding ${selectedClientsToAdd.length} client${selectedClientsToAdd.length > 1 ? 's' : ''}...`);
      
      // Add all selected clients
      for (const clientName of selectedClientsToAdd) {
        try {
          const response = await fetch(`${BACKEND_URL}/api/recruiter-clients/add`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              user_id: user.id,
              client_name: clientName
            })
          });
          
          if (response.ok) {
            const data = await response.json();
            // Check if it was already assigned by looking at the message
            if (data.message && data.message.includes('already assigned')) {
              results.alreadyAssigned.push(clientName);
            } else {
              results.added.push(clientName);
            }
          } else {
            results.failed.push(clientName);
          }
        } catch (error) {
          console.error(`Error adding client ${clientName}:`, error);
          results.failed.push(clientName);
        }
      }
      
      // Dismiss loading toast
      toast.dismiss(loadingToast);
      
      // Refresh the data from the server to get the complete list
      await fetchClientAssignments();
      
      // Show appropriate messages with longer duration
      if (results.added.length > 0) {
        toast.success(
          `Successfully added ${results.added.length} client${results.added.length > 1 ? 's' : ''} to ${user.full_name || user.username}!`,
          { duration: 4000 }
        );
      }
      if (results.alreadyAssigned.length > 0) {
        toast.info(
          `${results.alreadyAssigned.length} client${results.alreadyAssigned.length > 1 ? 's were' : ' was'} already assigned`,
          { duration: 3000 }
        );
      }
      if (results.failed.length > 0) {
        toast.error(
          `Failed to add ${results.failed.length} client${results.failed.length > 1 ? 's' : ''}`,
          { duration: 4000 }
        );
      }
      
      // If nothing happened, show a generic success
      if (results.added.length === 0 && results.alreadyAssigned.length === 0 && results.failed.length === 0) {
        toast.success('Operation completed', { duration: 3000 });
      }
      
      setShowAddModal(false);
      setSearchTerm('');
      setSelectedClientsToAdd([]);
      
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error adding clients:', error);
      toast.error('Failed to add clients. Please try again.');
    } finally {
      setIsAdding(false);
    }
  };

  const toggleClientSelection = (clientName) => {
    setSelectedClientsToAdd(prev => {
      if (prev.includes(clientName)) {
        return prev.filter(c => c !== clientName);
      } else {
        return [...prev, clientName];
      }
    });
  };

  const handleRemoveClient = async (clientName) => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const response = await fetch(`${BACKEND_URL}/api/recruiter-clients/remove`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: user.id,
          client_name: clientName
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to remove client');
      }
      
      const data = await response.json();
      setAssignedClients(data.assigned_clients);
      toast.success(`Removed ${clientName} from ${user.full_name || user.username}`);
      
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error removing client:', error);
      toast.error('Failed to remove client');
    }
  };

  if (!isOpen) return null;

  const unassignedClients = availableClients.filter(
    client => !assignedClients.includes(client)
  );

  const filteredUnassigned = unassignedClients.filter(client =>
    client.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-testid="client-assignment-modal">
      <div className="bg-white rounded-lg w-full max-w-3xl mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center">
            <Users className="h-6 w-6 text-blue-600 mr-3" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Manage Client Assignments
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {user?.full_name || user?.username} - {user?.role}
              </p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            data-testid="close-modal-button"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading client assignments...</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Assigned Clients Section */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                      <Building2 className="h-5 w-5 mr-2 text-green-600" />
                      Assigned Clients ({assignedClients.length})
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                      Clients currently assigned to this recruiter
                    </p>
                  </div>
                  <button
                    onClick={() => setShowAddModal(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    data-testid="add-client-button"
                  >
                    <Plus className="h-4 w-4" />
                    Add Client
                  </button>
                </div>

                {assignedClients.length === 0 ? (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
                    <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">No clients assigned yet</p>
                    <p className="text-sm text-gray-500 mt-2">
                      Click "Add Client" to assign clients to this recruiter
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {assignedClients.map((client, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors"
                        data-testid={`assigned-client-${idx}`}
                      >
                        <div className="flex items-center flex-1">
                          <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center bg-green-100 rounded-lg">
                            <Building2 className="h-5 w-5 text-green-600" />
                          </div>
                          <div className="ml-3">
                            <p className="text-sm font-medium text-gray-900">{client}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleRemoveClient(client)}
                          className="ml-2 p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Remove client"
                          data-testid={`remove-client-${idx}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add Client Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg w-full max-w-2xl mx-4 max-h-[80vh] overflow-hidden">
            <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Add Client</h3>
              <button 
                onClick={() => {
                  setShowAddModal(false);
                  setSearchTerm('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6">
              {/* Search and Action Bar */}
              <div className="flex items-center gap-3 mb-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search clients..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    data-testid="search-clients-input"
                  />
                </div>
                <button
                  onClick={() => {
                    if (selectedClientsToAdd.length === filteredUnassigned.length) {
                      setSelectedClientsToAdd([]);
                    } else {
                      setSelectedClientsToAdd([...filteredUnassigned]);
                    }
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors whitespace-nowrap"
                  data-testid="select-all-button"
                >
                  {selectedClientsToAdd.length === filteredUnassigned.length && filteredUnassigned.length > 0 ? 'Deselect All' : 'Select All'}
                </button>
                <button
                  onClick={handleAddClients}
                  disabled={selectedClientsToAdd.length === 0 || isAdding}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  data-testid="add-selected-clients-button"
                >
                  {isAdding ? (
                    <>
                      <RefreshCw className="h-4 w-4 animate-spin" />
                      Adding...
                    </>
                  ) : (
                    <>
                      <Check className="h-4 w-4" />
                      Add Selected ({selectedClientsToAdd.length})
                    </>
                  )}
                </button>
              </div>

              {/* Selection Helper */}
              <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold">{filteredUnassigned.length}</span> clients available
                    {searchTerm && ` (filtered from ${unassignedClients.length})`}
                    {selectedClientsToAdd.length > 0 && (
                      <span className="ml-2 text-blue-700">
                        • <span className="font-semibold">{selectedClientsToAdd.length}</span> selected
                      </span>
                    )}
                  </p>
                  {selectedClientsToAdd.length > 0 && (
                    <button
                      onClick={() => setSelectedClientsToAdd([])}
                      className="text-xs text-blue-600 hover:text-blue-800"
                    >
                      Clear selection
                    </button>
                  )}
                </div>
                {assignedClients.length > 0 && (
                  <p className="text-xs text-gray-600 mt-2">
                    Note: {assignedClients.length} client{assignedClients.length !== 1 ? 's are' : ' is'} already assigned and won't appear in this list
                  </p>
                )}
              </div>

              {/* Available Clients List with Checkboxes */}
              <div className="max-h-96 overflow-y-auto">
                {filteredUnassigned.length === 0 ? (
                  <div className="text-center py-8">
                    <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">
                      {searchTerm ? 'No clients found' : 'All clients are already assigned'}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {filteredUnassigned.map((client, idx) => {
                      const isSelected = selectedClientsToAdd.includes(client);
                      return (
                        <label
                          key={idx}
                          className={`w-full flex items-center justify-between p-4 border rounded-lg transition-colors cursor-pointer ${
                            isSelected 
                              ? 'bg-blue-50 border-blue-300' 
                              : 'bg-gray-50 hover:bg-gray-100 border-gray-200'
                          }`}
                          data-testid={`available-client-${idx}`}
                        >
                          <div className="flex items-center flex-1">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleClientSelection(client)}
                              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mr-3"
                            />
                            <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center bg-blue-100 rounded-lg">
                              <Building2 className="h-5 w-5 text-blue-600" />
                            </div>
                            <div className="ml-3">
                              <p className="text-sm font-medium text-gray-900">{client}</p>
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientAssignmentModal;
